import { create } from "@bufbuild/protobuf";
import { type LanguageRegions, ChatPrompt, RichText, Scope, Chat } from "./gen";
import { createScope } from "./registry";

/**
 * Creates a StreamRequest for starting a new chat with rich text content.
 * If rich text, rich text documents may be returned for some stop messages
 * representing the entire message in rich text format.
 *
 * @param chatId - Chat ID (empty string for new chat)
 * @param text - The text content to send
 * @param languageRegion - Language region for the chat
 * @param plainText - Whether the text is plain text (default: false)
 * @param scope - Optional scope information for knowledge filtering
 * @param projectId - Project ID for the chat to be saved to
 * @param workspaceId - Workspace ID for the chat to be saved to
 * @returns A properly constructed StreamRequest
 */
export function newStreamRequest(
  chatId: string,
  text: string | RichText.RichText,
  languageRegion: LanguageRegions.LanguageRegion,
  plainText: boolean,
  projectId: string,
  workspaceId: string,
  scope?: Scope.Scope,
  parentMessageId?: string,
): Chat.StreamRequest {
  if (plainText) {
    return create(Chat.StreamRequestSchema, {
      prompt: create(ChatPrompt.PromptSchema, {
        chatId,
        languageRegion,
        parentMessageId,
        prompt:
          typeof text === "string"
            ? {
                case: "text",
                value: text,
              }
            : {
                case: "richText",
                value: text,
              },
        scope,
        recordSession: true,
        projectId: projectId,
        workspaceId: workspaceId,
      }),
    });
  }

  return create(Chat.StreamRequestSchema, {
    prompt: create(ChatPrompt.PromptSchema, {
      chatId,
      languageRegion,
      parentMessageId,
      projectId: projectId,
      workspaceId: workspaceId,
      prompt: {
        case: "richText",
        value:
          typeof text === "string"
            ? create(RichText.RichTextSchema, {
                type: "doc",
                content: [
                  create(RichText.NodeSchema, {
                    type: "paragraph",
                    content: [
                      create(RichText.NodeSchema, {
                        type: "text",
                        text,
                      }),
                    ],
                  }),
                ],
                attrs: create(RichText.DocAttrsSchema, {
                  title: "",
                  createdAt: BigInt(Date.now()),
                  updatedAt: BigInt(Date.now()),
                  version: "1.0",
                }),
              })
            : {
                ...text,
                attrs: create(RichText.DocAttrsSchema, {
                  title: "",
                  createdAt: BigInt(Date.now()),
                  updatedAt: BigInt(Date.now()),
                  version: "1.0",
                }),
              },
      },
      scope,
      recordSession: true,
    }),
  });
}

/**
 * Creates a StreamPlaybackRequest for playing back a previously streamed message.
 *
 * @param messageId - The ID of the message to play back (Format: ^message_[a-z0-9]{20}$)
 * @param playbackSpeed - Playback speed multiplier (1.0 = normal speed, 0.5 = half speed, 2.0 = double speed)
 * @returns A properly constructed StreamPlaybackRequest
 */
export function newStreamPlaybackRequest(messageId: string, playbackSpeed: number): Chat.StreamPlaybackRequest {
  return create(Chat.StreamPlaybackRequestSchema, {
    messageId,
    playbackSpeed,
  });
}

/**
 * Creates a protobuf Scope object from a simple JavaScript object.
 * This utility helps avoid importing @bufbuild/protobuf directly in application code.
 *
 * @param scope - Simple scope object with asset IDs and optional project IDs
 * @returns A properly constructed protobuf Scope object
 */
export function newScope(scope: {
  assetIdList: string[];
  projectIdList?: string[];
  conceptIdList?: string[];
}): Scope.Scope {
  return create(Scope.ScopeSchema, {
    assetIdList: scope.assetIdList,
    projectIdList: scope.projectIdList || [],
    conceptIdList: scope.conceptIdList || [],
  });
}
